export interface FieldCondition {
  id: string
  sourceField: string
  sourceFieldId?: number | null
  sourceType: 'standard' | 'custom'
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty' | 'is_one_of'
  value: string
  action: 'show' | 'hide' | 'require' | 'unrequire'
}

export interface RegistrationField {
  key: string
  order: number
  custom: boolean
  width: number
  required: boolean
  uniquePerEvent: boolean
  startNewRow?: boolean
  hidden: boolean
  conditions: FieldCondition[]
}

export interface ChoiceStat {
  label: string
  count: number
}

export interface CustomFieldDef {
  id?: number | null
  name: string
  nameEn?: string
  nameAr?: string
  fieldType: string
  required: boolean
  order: number
  options: string[]
  width: number
  uniquePerEvent: boolean
  startNewRow?: boolean
  hidden: boolean
  conditions: FieldCondition[]
  showChoiceStats?: boolean
  choiceStats?: ChoiceStat[]
  optionIconUrls?: Array<string | null>
}

export interface EventTheme {
  id: number
  name: string
  about: string | null
  ended: boolean
  primaryColor: string
  secondaryColor: string
  font: string | null
  customFontUrl: string | null
  customFontName: string | null
  bannerUrl: string | null
  mobileBannerUrl?: string | null
  footerUrl: string | null
  desktopDesignUrl: string | null
  mobileDesignUrl: string | null
  logoUrl: string | null
  regBgColor: string
  regTextColor: string
  regCardBgColor: string
  regButtonColor: string
  regButtonTextColor: string
  regInputBorderColor: string
  regFormWidth: string
  regFormAlignment: string
  regMobileFormAlignment: string
  regShowLangSwitcher: boolean
  regCustomCss: string
  regHeaderHtml: string
  regFooterHtml: string | null
  regHeaderPlacement?: 'inside' | 'outside'
  regFooterPlacement?: 'inside' | 'outside'
  privacyPolicyHtml: string | null
}

export interface InitialFormValues {
  title?: string | null
  otherTitle?: string | null
  firstName?: string | null
  lastName?: string | null
  phoneNumber?: string | null
  email?: string | null
  organization?: string | null
  position?: string | null
  userType?: string | null
  gender?: string | null
  country?: string | null
  city?: string | null
  age?: string | null
  idNumber?: string | null
  passportNumber?: string | null
  bloodType?: string | null
  customFields?: Record<string, string>
  imageUrl?: string | null
}

export const FIELD_MAP: Record<string, string> = {
  'Title': 'title',
  'First name': 'firstName',
  'Last name': 'lastName',
  'Phone number': 'phoneNumber',
  'Email': 'email',
  'Organization': 'organization',
  'Position': 'position',
  'User type': 'userType',
  'Gender': 'gender',
  'Country': 'country',
  'City': 'city',
  'Age': 'age',
  'Id number': 'idNumber',
  'Passport number': 'passportNumber',
  'Blood type': 'bloodType',
  'Image': 'image',
}

export function normalizeConditions(value: unknown): FieldCondition[] {
  if (Array.isArray(value)) return value
  if (!value) return []

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  return []
}

export function normalizeConditionText(value: string): string {
  return value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLocaleLowerCase()
}

export function splitConditionValues(value: string): string[] {
  return value
    .split(/[\n,;،؛]+/u)
    .map(normalizeConditionText)
    .filter(Boolean)
}

export interface TitleOption {
  value: string
  label: string
  femaleValue?: string
}

export function isFemaleGender(gender: string) {
  const value = gender.trim().toLowerCase()
  return value === 'female' || value === 'أنثى' || value === 'انثى'
}

export function visibleTitleOptions(options: TitleOption[], locale: string) {
  if (locale === 'ar') return options
  const seen = new Set<string>()
  return options.filter((option) => {
    if (seen.has(option.label)) return false
    seen.add(option.label)
    return true
  })
}

export function titleSelectValue(options: TitleOption[], stored: string) {
  const match = options.find((option) => option.value === stored || option.femaleValue === stored)
  return match?.value || stored
}

export function genderedTitleValue(options: TitleOption[], value: string, gender: string) {
  if (!value || value === 'other') return value
  const match = options.find((option) => option.value === value || option.femaleValue === value)
  if (!match) return value
  if (isFemaleGender(gender)) return match.femaleValue || match.value
  return match.value
}

export function emptyFormState(initial?: InitialFormValues | null) {
  return {
    title: initial?.title || '',
    otherTitle: initial?.otherTitle || '',
    firstName: initial?.firstName || '',
    lastName: initial?.lastName || '',
    phoneNumber: initial?.phoneNumber || '',
    email: initial?.email || '',
    organization: initial?.organization || '',
    position: initial?.position || '',
    userType: initial?.userType || '',
    gender: initial?.gender || '',
    country: initial?.country || '',
    city: initial?.city || '',
    age: initial?.age || '',
    idNumber: initial?.idNumber || '',
    passportNumber: initial?.passportNumber || '',
    bloodType: initial?.bloodType || '',
    customFields: initial?.customFields || {},
  }
}
